import {
  CustomReport,
  CustomReportModules,
} from 'src/services/customReportsApi/interfaces/customReports.interface'

// The six feature modules a report can be generated from.
export type ReportModule =
  | 'ad'
  | 'ai'
  | 'content'
  | 'inbox'
  | 'review'
  | 'social'

// generating  — the PDF is still being produced.
// ready       — generated and downloadable.
// downloading — client-side only: the user has started a download. The backend
//               has no such status; it lives in component state.
// failed      — generation did not complete; the card becomes a retry banner.
export type ReportStatus = 'downloading' | 'failed' | 'generating' | 'ready'

// The widget's view of a report, mapped from the API shape by `toReport`.
export interface Report {
  // Human-readable range the report covers, e.g. "Jun 1 – Jun 30, 2026".
  dateRange: string
  // Present once generated, e.g. "2.4 MB".
  fileSize?: string
  // Present once generated, e.g. "Jul 1, 2026".
  generatedAt?: string
  id: string
  modules: ReportModule[]
  name: string
  // 0–100 while generating, when the source knows how far along it is. Absent
  // for PDF reports, whose render reports no progress.
  progress?: number
  status: ReportStatus
}

// Maps the API's per-module id arrays onto the widget's flat module list, which
// is all the pills need.
const MODULE_BY_FIELD: [keyof CustomReportModules, ReportModule][] = [
  ['socialListeningCampaignIds', 'social'],
  ['aiListeningCampaignIds', 'ai'],
  ['inboxPageIds', 'inbox'],
  ['reviewAnalysisCampaignIds', 'review'],
  ['contentAnalysisIds', 'content'],
  ['adAnalysisPageIds', 'ad'],
]

const formatDate = (value?: string) =>
  value
    ? new Date(value).toLocaleDateString('en-US', {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
      })
    : undefined

// Bytes → "2.4 MB". Falls back to KB below a megabyte so a small report does
// not read as "0.0 MB". Exported because Campaign Comparison reuses this card
// and its sizes should read identically.
export const formatFileSize = (bytes?: number) => {
  if (!bytes) return undefined
  const mb = bytes / 1024 / 1024

  return mb < 1 ? `${Math.round(bytes / 1024)} KB` : `${mb.toFixed(1)} MB`
}

export const toReport = (report: CustomReport): Report => ({
  dateRange: [
    formatDate(report.dateRange?.from),
    formatDate(report.dateRange?.to),
  ]
    .filter(Boolean)
    .join(' – '),
  fileSize: formatFileSize(report.fileSize),
  generatedAt: formatDate(report.generatedAt),
  id: report.id,
  modules: MODULE_BY_FIELD.filter(
    ([field]) => (report.modules?.[field] || []).length > 0
  ).map(([, module]) => module),
  name: report.name,
  status: report.status,
})
